import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authClient } from "../auth-client";
import { Brand } from "../components/Brand";

const inputCls =
  "w-full h-11 px-3.5 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/60 transition-colors";

export function AccountSetup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const renewalToken = searchParams.get("renewalToken") ?? "";
  const status = searchParams.get("status") ?? "";
  const authError = searchParams.get("error") ?? "";
  const adminSetup = searchParams.get("admin") === "1";
  const signInPath = adminSetup ? "/admin/login" : "/login";
  const adminQuery = adminSetup ? "&admin=1" : "";
  const automaticallyRenewing = Boolean(authError && renewalToken);
  const invalidLink = status === "invalid" || Boolean(authError) || !token;
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [working, setWorking] = useState(false);
  const [complete, setComplete] = useState(status === "complete");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!automaticallyRenewing) return;
    window.location.replace(
      `/api/invitations/account-setup/${encodeURIComponent(renewalToken)}?renew=1${adminQuery}`,
    );
  }, [adminQuery, automaticallyRenewing, renewalToken]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (password.length < 15) {
      setError("Use a passphrase of at least 15 characters.");
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setWorking(true);
    try {
      const result = await authClient.resetPassword({ newPassword: password, token });
      if (result.error) {
        window.location.assign(
          `/api/invitations/account-setup/${encodeURIComponent(token)}?renew=1${adminQuery}`,
        );
        return;
      }
      setComplete(true);
    } catch {
      setError("Your account could not be completed. Check your connection and try again.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px]">
        <div className="flex justify-center mb-8">
          <Brand logoHeight={62} align="center" />
        </div>
        <div className="bg-surface-white border border-default rounded-xl px-[30px] py-[30px]">
          {complete ? (
            <div role="status" className="flex flex-col items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[rgba(86,205,174,0.14)] text-status-green flex items-center justify-center">
                <i className="fa-solid fa-check" aria-hidden="true" />
              </span>
              <div>
                <h1 className="text-title-md font-bold text-primary">Account setup complete</h1>
                <p className="text-body text-secondary mt-1">
                  Your email is verified and your password has been saved.
                </p>
              </div>
              <Link
                to={signInPath}
                className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold flex items-center justify-center"
              >
                Sign in
              </Link>
            </div>
          ) : status === "expired" ? (
            <div role="status" className="flex flex-col items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-brand-red-tint text-brand-red flex items-center justify-center">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </span>
              <div>
                <h1 className="text-title-md font-bold text-primary">Setup link expired</h1>
                <p className="text-body text-secondary mt-1">
                  We have automatically sent a new account setup email to the address originally
                  invited. The new one-time link will be valid for 24 hours.
                </p>
              </div>
              <Link
                to={signInPath}
                className="text-body font-semibold text-brand-red hover:underline"
              >
                Return to sign in
              </Link>
            </div>
          ) : status === "delivery-error" ? (
            <div role="alert" className="flex flex-col items-start gap-4">
              <h1 className="text-title-md font-bold text-primary">New link could not be sent</h1>
              <p className="text-body text-secondary">
                We could not send a replacement setup email just now. Please try the link again or
                contact your FMC representative if the problem continues.
              </p>
              <Link
                to={signInPath}
                className="text-body font-semibold text-brand-red hover:underline"
              >
                Return to sign in
              </Link>
            </div>
          ) : automaticallyRenewing ? (
            <div role="status" className="flex flex-col items-start gap-4">
              <h1 className="text-title-md font-bold text-primary">Refreshing your setup link</h1>
              <p className="text-body text-secondary">
                This link can no longer be used. We are sending a new 24-hour setup link to the
                address originally invited.
              </p>
            </div>
          ) : invalidLink ? (
            <div role="alert" className="flex flex-col items-start gap-4">
              <h1 className="text-title-md font-bold text-primary">Setup link unavailable</h1>
              <p className="text-body text-secondary">
                This one-time link is invalid or has already been replaced. Please use the latest
                account setup email, or ask your FMC contact to send a new one.
              </p>
              <Link
                to={signInPath}
                className="text-body font-semibold text-brand-red hover:underline"
              >
                Return to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div>
                <h1 className="text-title-md font-bold text-primary">Complete your account</h1>
                <p className="text-body text-secondary mt-1">
                  Choose a password to verify your email and activate Leads Manager access.
                </p>
              </div>
              <label className="flex flex-col gap-1.5 text-caption font-medium text-primary">
                Password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={15}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-caption font-medium text-primary">
                Confirm password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={15}
                  required
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className={inputCls}
                />
              </label>
              <p className="text-micro text-tertiary">
                Use at least 15 characters. Common or breached passwords are rejected.
              </p>
              {error && (
                <div
                  role="alert"
                  className="rounded-xs bg-[rgba(255,101,80,0.1)] px-3 py-2.5 text-caption text-status-red"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={working}
                aria-busy={working}
                className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold disabled:opacity-50"
              >
                {working ? "Saving…" : "Complete account setup"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
